import { useState } from 'react';

// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import Header from '~/components/Header';
import TryForFreeButton from '~/components/TryForFreeButton';
import { createLogger } from '~/utils/logger.server';

export const handle = {
  title: '5 Speaking Habits That Boost Your Sales Performance | Pavone Academy',
  description: 'Learn how to sound confident on sales calls, improve your tone of voice, and close more deals with these proven speaking techniques used by top sales performers.',
};

const logger = createLogger('AcademySalesArticle');

export async function loader(args: LoaderFunctionArgs) {
  await logger.info('Academy sales article loaded');
  return Response.json({});
}

export const meta: MetaFunction = () => {
  return [
    { title: '5 Speaking Habits That Boost Sales Performance - Pavone' },
    { 
      name: 'description', 
      content: 'Discover how to sound confident on sales calls and close more deals. Expert tips on tone of voice, pacing, and communication that top salespeople use.' 
    },
    { name: 'keywords', content: 'how to sound confident on sales calls, improve sales communication, tone of voice in sales, sales pitch practice, AI sales coaching, sales speaking skills' },
    { property: 'og:title', content: '5 Speaking Habits That Instantly Boost Your Sales Performance' },
    { property: 'og:description', content: 'Learn how top salespeople sound confident on calls and close more deals through better communication.' },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: '5 Speaking Habits That Boost Your Sales Performance' },
    { name: 'twitter:description', content: 'Master the speaking habits that help you sound confident on sales calls and close more deals.' },
  ];
};

export default function SalesPerformanceArticle() {
  const [isShared, setIsShared] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const title = '5 Speaking Habits That Instantly Boost Your Sales Performance';
    const text = 'Check out these speaking habits that boost sales performance:';

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        setTimeout(() => setIsShared(false), 2000);
      } else {
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsShared(false), 2000);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Academy</span>
          </Link>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 rounded-2xl overflow-hidden"
          >
            <img
              src="/images/academy/sales.jpg"
              alt="Sales communication confidence"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                Sales & Business
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>8 min read</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark-blue mb-6 leading-tight">
              5 Speaking Habits That Instantly Boost Your Sales Performance
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              The communication secrets that separate top performers from average salespeople — 
              and how to sound confident on sales calls even when you're nervous.
            </p>

            <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-12">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Published:</span> November 3, 2024
              </div>
              <button
                onClick={handleShare}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isShared
                    ? 'text-brand-green'
                    : 'text-gray-600 hover:text-brand-dark-blue'
                }`}
              >
                {isShared ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I once sat next to our company's top salesperson during a cold call session. Same product. Same script. 
              Same prospects. But while I struggled to get past the gatekeeper, he was booking meetings left and right. 
              The difference wasn't what he was saying — it was <strong>how he was saying it</strong>.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Here's what nobody tells you about sales: <strong>learning how to sound confident on sales calls matters 
              more than having the perfect pitch.</strong> Your tone, pacing, energy, and vocal delivery can make the 
              difference between "not interested" and "tell me more." I've trained hundreds of sales reps, and the ones 
              who master these five speaking habits consistently outperform everyone else.
            </p>

            {/* Habit 1 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Habit #1: Control Your Pace (Slow Down to Speed Up)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When you're nervous or excited about a sale, your instinct is to talk faster. You want to get all your 
              points across before they hang up. But here's the paradox: <strong>the faster you talk, the less 
              trustworthy you sound.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I had a sales rep, David, who was crushing his product knowledge but barely hitting 50% of quota. When 
              I listened to his calls, I immediately heard the problem — he was speaking at lightning speed, rushing 
              through his pitch like he was afraid the prospect would interrupt. To him, it felt normal. To prospects, 
              it sounded desperate.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              We worked on one simple change: pause after asking a question. Not a quick breath — a real, 3-second pause. 
              The first week felt agonizing to him. The second week, his close rate jumped 23%. Prospects started saying 
              things like "You really seem to understand what we need."
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              How to Improve Sales Communication Through Pacing
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Speak 20% slower than feels natural.</strong> Record your calls and listen back. 
                You'll realize you're still talking faster than you think.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Use strategic pauses before answering objections.</strong> When a prospect says 
                "that's too expensive," don't rush to defend. Pause. Think. Then respond thoughtfully.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Let silence work for you after questions.</strong> After "Does that make sense?" 
                or "What do you think?" — shut up. Give them space to process and respond.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Match their energy, then lead it slightly lower.</strong> If they're rushed, 
                don't mirror that. Be calm. Your controlled pace will influence theirs.</span>
              </li>
            </ul>

            <p className="text-lg leading-relaxed text-gray-700 mb-8 bg-brand-gold/5 p-6 rounded-lg border-l-4 border-brand-gold">
              💡 <strong>Pro tip:</strong> The best closers I know speak like they've already won the deal. There's 
              no urgency in their voice because they're confident. Slow, calm pacing projects that confidence.
            </p>

            {/* Habit 2 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Habit #2: Master Your Tone of Voice in Sales
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Your tone carries more meaning than your words. You can say "I'd love to help you" with genuine warmth 
              or with the enthusiasm of reading a phone book. Prospects feel the difference immediately.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              <strong>The three tones that kill sales:</strong>
            </p>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">1. The Apologetic Tone</h4>
                <p>"Sorry to bother you..." "I know you're busy, but..." This immediately positions you as an 
                interruption rather than a solution.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">2. The Aggressive Tone</h4>
                <p>Pushing too hard, talking over objections, sounding combative. This triggers fight-or-flight 
                and prospects shut down.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">3. The Monotone Robot</h4>
                <p>Reading from a script with zero inflection. Prospects tune out in seconds because nothing 
                sounds important.</p>
              </div>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Winning Tone: Curious + Confident
            </h3>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The best sales tone sounds like you're genuinely curious about their situation and confident you can 
              help. Not aggressive. Not apologetic. Just interested and certain.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Practice this: Record yourself saying "Tell me more about that challenge" in three different tones — 
              monotone, overly enthusiastic, and genuinely curious. The difference is dramatic. The curious tone 
              invites conversation. The others shut it down.
            </p>

            {/* Habit 3 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Habit #3: Drop Your Pitch (Literally)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              High-pitched voices sound nervous, uncertain, and young. Low-pitched voices sound confident, authoritative, 
              and trustworthy. It's not fair, but it's true.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I worked with a female sales rep, Christina, who had incredible product knowledge but prospects kept 
              saying she "seemed inexperienced." When we analyzed her calls, her voice pitch jumped up whenever she 
              got excited or nervous — which made her sound less credible.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              We didn't try to change her natural voice. We just focused on <strong>keeping her pitch steady</strong> 
              instead of letting it rise at the end of sentences (upspeak). Within two months, prospects stopped 
              questioning her expertise. Same person, same knowledge, different vocal delivery.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              How to Lower Your Pitch on Calls
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Breathe from your diaphragm before calls.</strong> Deep belly breaths naturally 
                lower your voice and calm your nerves.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Speak from your chest, not your throat.</strong> Put your hand on your chest — 
                you should feel vibration when you talk.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>End statements on a downward inflection.</strong> Don't make statements sound like 
                questions. "This could save you 20%" should go down, not up.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Record yourself and practice.</strong> Hearing your own voice helps you adjust 
                without overthinking it during live calls.</span>
              </li>
            </ul>

            {/* Habit 4 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Habit #4: Eliminate Filler Words and Hesitations
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              "Um, so basically, you know, like, I mean..." Every filler word chips away at your credibility. 
              <strong>Confident people don't fill silence with verbal static.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The worst part? Most salespeople don't even realize how often they do it. I once counted 47 "ums" 
              in a 10-minute sales call. When I played it back for the rep, he was shocked. He genuinely thought 
              he sounded smooth.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's what changed for him: instead of filling pauses with "um" while thinking, he learned to just... 
              pause. No sound. Just silence. At first it felt awkward. But prospects didn't notice the pauses. 
              They noticed the confidence.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Breaking the Filler Word Habit
            </h3>

            <div className="bg-brand-dark-blue/5 p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">1.</span>
                  <span><strong>Identify your personal fillers.</strong> Record 3-5 calls and list every filler 
                  word you use. Awareness is the first step.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">2.</span>
                  <span><strong>Practice the "pause instead" technique.</strong> When you feel "um" coming, 
                  force yourself to be silent for 1-2 seconds instead.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">3.</span>
                  <span><strong>Slow down your overall pace.</strong> Filler words multiply when you're rushing. 
                  Slow speakers rarely need fillers.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">4.</span>
                  <span><strong>Use AI sales coaching tools to track progress.</strong> Tools like Pavone can 
                  automatically count filler words and show improvement over time.</span>
                </li>
              </ul>
            </div>

            {/* Habit 5 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Habit #5: How to Sound Confident on Sales Calls Through Energy Management
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Your energy level sets the tone for the entire conversation. Too low, and you sound disinterested. 
              Too high, and you sound fake or desperate. <strong>Top performers match energy strategically.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's the secret: start slightly above their energy level to lift the conversation, then settle into 
              a calm, confident baseline. You're not matching their energy — you're leading it.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Energy Formula for Sales Calls
            </h3>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">Opening (0-30 seconds): 7/10 Energy</h4>
                <p>Start upbeat and engaged. Not crazy enthusiastic, but noticeably positive. This creates momentum 
                and puts them at ease.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">Discovery (1-5 minutes): 5/10 Energy</h4>
                <p>Calm, curious, focused. You're listening more than talking. Lower energy here shows you're 
                genuinely interested in understanding, not just pitching.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">Presentation (5-12 minutes): 6/10 Energy</h4>
                <p>Pick up energy when discussing solutions that match their needs. Show enthusiasm about solving 
                their specific problem, not just reading features.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">Close (Final 2 minutes): 5/10 Energy</h4>
                <p>Stay calm and confident. If you sound desperate to close, they'll sense it. Assume the sale 
                and speak like it's already decided.</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Practice this: Before your next call, do 10 jumping jacks. Sounds ridiculous, but it gets your energy 
              up naturally without forcing fake enthusiasm. Then take 3 deep breaths to bring it down to that 
              confident baseline. Now you're ready.
            </p>

            {/* Practice Section */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              How to Practice Sales Pitch Online and Improve Faster
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Reading these habits won't change your sales numbers. <strong>Practicing them will.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The best sales teams I work with do weekly call practice sessions. Not just listening to calls — 
              actively practicing different scenarios out loud. Role plays. Recorded practice pitches. AI-powered 
              feedback tools that analyze their tone, pace, and filler words.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              The #1 mistake I see? Salespeople only practice during real calls with real prospects. That's like 
              a basketball player only practicing during games. You need reps in a safe environment where mistakes 
              don't cost you deals.
            </p>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-brand-dark-blue to-brand-gold p-8 rounded-2xl text-white my-12">
              <h3 className="text-2xl font-bold mb-4">
                Practice Your Sales Pitch with AI Feedback
              </h3>
              <p className="text-lg mb-6 text-white/90">
                Use Pavone to record your sales pitches and get instant AI feedback on pace, tone, filler words, 
                and confidence level. See exactly what prospects hear — and improve before your next call.
              </p>
              <TryForFreeButton size="md" />
            </div>

            {/* Conclusion */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Your Next Steps
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              These five speaking habits separate quota-crushers from quota-misers. But here's the thing: <strong>you 
              can't change all five at once.</strong> Pick one. Master it over two weeks. Then add the next.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Start with pace. It's the easiest to control and has the biggest immediate impact. Record your next 
              three calls. Listen back. Count how many times you rushed through key points. Then slow down by 20% 
              on your next calls.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              If you want to know exactly <strong>how to sound confident on sales calls</strong>, the answer isn't 
              a secret technique. It's consistent practice with objective feedback. Master these five habits, and 
              watch your close rate climb.
            </p>

            {/* FAQ Section */}
            <div className="mt-16 mb-12">
              <h2 className="text-3xl font-bold text-brand-dark-blue mb-8">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How can I improve my tone on sales calls?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Record your calls and listen for monotone delivery, apologetic language, or aggressive pushing. 
                    Practice speaking with genuine curiosity and confidence. Smile while you talk (it changes your 
                    tone naturally), breathe deeply to lower your pitch, and match your prospect's energy level 
                    before leading it slightly lower. The key is sounding interested and certain, not desperate or robotic.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Can AI help me sound more confident in sales?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Absolutely. AI sales coaching tools can analyze your pace, tone, filler words, and confidence 
                    level in real-time. They provide objective feedback that's hard to get otherwise — you can't hear 
                    yourself the way prospects do. Tools like Pavone let you practice sales pitches online and track 
                    improvement over time. It's like having a sales coach analyzing every practice session.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What makes a sales voice persuasive?
                  </h3>
                  <p className="text-lg text-gray-700">
                    A persuasive sales voice has five key qualities: controlled pacing (not rushed), steady pitch 
                    (no upspeak), genuine tone (curious and confident), minimal filler words (clean delivery), and 
                    appropriate energy (matched to context). It's not about having a naturally "good" voice — it's 
                    about controlling the voice you have. The most persuasive salespeople sound like they've already 
                    won the deal because they're calm and confident.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How to avoid sounding scripted when selling?
                  </h3>
                  <p className="text-lg text-gray-700">
                    The problem isn't the script — it's how you deliver it. Practice your script so many times that 
                    you can say it naturally without thinking. Vary your pacing and tone throughout (don't maintain 
                    the same energy level). Use strategic pauses to sound thoughtful rather than pre-planned. Most 
                    importantly, actually listen to responses and react authentically. If you're genuinely curious 
                    about their answers, you won't sound scripted.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How often should I practice my sales pitch to improve?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Top performers practice their pitch at least 3-5 times per week outside of live calls. Think of 
                    it like sports training — you don't just play games, you practice fundamentals. Record yourself 
                    delivering different parts of your pitch, focusing on one speaking habit at a time. Use AI 
                    coaching tools to track progress on specific metrics like pace, filler words, or tone. You should 
                    see noticeable improvement within 2 weeks of consistent practice.
                  </p>
                </div>
              </div>
            </div>

            {/* Long-tail Keywords Section (hidden from users, for SEO) */}
            <div className="sr-only">
              <h2>Related Topics and Keywords</h2>
              <ul>
                <li>how to sound confident on sales calls without being pushy</li>
                <li>improve sales communication skills for closing deals</li>
                <li>tone of voice training for sales professionals</li>
                <li>best AI sales coaching tools for practice</li>
                <li>practice sales pitch online with instant feedback</li>
                <li>eliminate filler words in sales presentations</li>
                <li>speaking habits that boost B2B sales performance</li>
                <li>how to sound more persuasive on cold calls</li>
              </ul>
            </div>
          </motion.div>

          {/* Author/Date Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">Pavone Academy</p>
                <p>Expert communication coaching and sales performance tips</p>
              </div>
              <Link
                to="/academy"
                className="text-brand-dark-blue hover:text-brand-gold font-medium transition-colors"
              >
                Read More Articles →
              </Link>
            </div>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

